import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  getFirebaseAuthService,
  type AccountType,
  type AuthIdentity,
  type AuthService,
} from "./authService";
import {
  getOrCreateGuestNickname,
  getStoredPublicNickname,
  storePublicNickname,
  validatePublicNickname,
} from "./publicNickname";
import {
  getPlayerDataService,
  type PlayerDataService,
  type PublicPlayerProfile,
} from "../data/playerDataService";

export type AuthStatus = "loading" | "ready" | "unconfigured";
export type AuthAction = "idle" | "anonymous" | "google" | "nickname" | "signOut";

export interface PlayerAccount {
  uid: string;
  accountType: AccountType;
  publicNickname: string | null;
}

interface NicknameResult {
  ok: boolean;
  message?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  action: AuthAction;
  account: PlayerAccount | null;
  error: string | null;
  signInAnonymously(): Promise<PlayerAccount | null>;
  signInWithGoogle(): Promise<PlayerAccount | null>;
  signOut(): Promise<void>;
  setPublicNickname(value: string): Promise<NicknameResult>;
  clearError(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toPlayerAccount(identity: AuthIdentity): PlayerAccount {
  return {
    ...identity,
    publicNickname:
      identity.accountType === "anonymous"
        ? getOrCreateGuestNickname(identity.uid)
        : getStoredPublicNickname(identity.uid),
  };
}

function toServerPlayerAccount(profile: PublicPlayerProfile): PlayerAccount {
  return {
    uid: profile.uid,
    accountType: profile.accountType,
    publicNickname: profile.publicAlias,
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "A fiókművelet most nem sikerült. Kérjük, próbáld újra.";
}

export function AuthProvider({
  children,
  service,
  dataService,
}: {
  children: ReactNode;
  service?: AuthService | null;
  dataService?: PlayerDataService | null;
}) {
  const resolvedService = useMemo(
    () => (service === undefined ? getFirebaseAuthService() : service),
    [service],
  );
  const resolvedDataService = useMemo(
    () => (dataService === undefined ? getPlayerDataService() : dataService),
    [dataService],
  );
  const [status, setStatus] = useState<AuthStatus>(resolvedService ? "loading" : "unconfigured");
  const [action, setAction] = useState<AuthAction>("idle");
  const [account, setAccount] = useState<PlayerAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!resolvedService) {
      setStatus("unconfigured");
      setAccount(null);
      return;
    }

    setStatus("loading");
    let active = true;
    const unsubscribe = resolvedService.subscribe((identity) => {
      if (!identity) {
        setAccount(null);
        setStatus("ready");
        return;
      }

      setStatus("loading");
      void hydrateAccount(identity)
        .then((nextAccount) => {
          if (active) {
            setAccount(nextAccount);
            setStatus("ready");
          }
        })
        .catch((operationError) => {
          if (active) {
            setAccount(toPlayerAccount(identity));
            setError(errorMessage(operationError));
            setStatus("ready");
          }
        });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [resolvedService]);

  async function hydrateAccount(identity: AuthIdentity): Promise<PlayerAccount> {
    if (!resolvedDataService) {
      return toPlayerAccount(identity);
    }
    return toServerPlayerAccount(await resolvedDataService.ensurePlayerProfile());
  }

  async function runSignIn(
    nextAction: Extract<AuthAction, "anonymous" | "google">,
    operation: () => Promise<AuthIdentity>,
  ): Promise<PlayerAccount | null> {
    if (!resolvedService) {
      setError("A Firebase fejlesztői konfiguráció még nincs megadva.");
      return null;
    }

    setAction(nextAction);
    setError(null);
    try {
      const nextAccount = await hydrateAccount(await operation());
      setAccount(nextAccount);
      setStatus("ready");
      return nextAccount;
    } catch (operationError) {
      setError(errorMessage(operationError));
      return null;
    } finally {
      setAction("idle");
    }
  }

  async function handleSignOut(): Promise<void> {
    if (!resolvedService) {
      return;
    }

    setAction("signOut");
    setError(null);
    try {
      await resolvedService.signOut();
      setAccount(null);
    } catch (operationError) {
      setError(errorMessage(operationError));
    } finally {
      setAction("idle");
    }
  }

  async function handlePublicNickname(value: string): Promise<NicknameResult> {
    if (!account || account.accountType !== "google") {
      return { ok: false, message: "Becenevet bejelentkezett Google-játékos állíthat be." };
    }

    const validation = validatePublicNickname(value);
    if (!validation.ok) {
      return { ok: false, message: validation.message };
    }

    setAction("nickname");
    setError(null);
    try {
      if (resolvedDataService) {
        const profile = await resolvedDataService.claimPlayerAlias(validation.nickname);
        setAccount(toServerPlayerAccount(profile));
      } else {
        storePublicNickname(account.uid, validation.nickname);
        setAccount({ ...account, publicNickname: validation.nickname });
      }
      return { ok: true };
    } catch (operationError) {
      return { ok: false, message: errorMessage(operationError) };
    } finally {
      setAction("idle");
    }
  }

  const value: AuthContextValue = {
    status,
    action,
    account,
    error,
    signInAnonymously: () =>
      runSignIn("anonymous", () => resolvedService!.signInAnonymously()),
    signInWithGoogle: () => runSignIn("google", () => resolvedService!.signInWithGoogle()),
    signOut: handleSignOut,
    setPublicNickname: handlePublicNickname,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
